import React, {
	createContext,
	FC,
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useState
} from 'react';

import { FullPageError } from 'src/common/components/errors/full-page-error/FullPageError';
import { ITenant, ProductType, TenantProduct } from 'src/common/interfaces/Tenant';
import { Logger } from 'src/common/services/Logger';

import { IUser, ROLE_TYPE_ENTITY } from '../../interfaces/User';
import { isLoggedIn } from '../../services/AuthenticationService';
import { getUserTenant } from '../../services/TenantsService';
import { getUserMetadata } from '../../services/UserService';

import { USER_TENANT_PROVIDER_LABELS } from './UserTenantProvider.labels';

interface IUserTenant {
	user: IUser | null;
	tenant: ITenant | null;
	hasAdminPermissions: () => boolean;
	updateUserTenant: (force?: boolean) => Promise<void>;
	getProductIdByType: (type: ProductType) => string;
}

const UserTenantContext = createContext<IUserTenant | null>(null);

interface UserTenantProviderProps {
	children: ReactNode;
}

const UserTenantProvider: FC<UserTenantProviderProps> = ({ children }) => {
	const [user, setUser] = useState<IUser | null>(null);
	const [tenant, setTenant] = useState<ITenant | null>(null);
	const [hasLoadingError, setHasLoadingError] = useState(false);

	const getProductIdByType = useCallback(
		(productType: ProductType): string => {
			const product = tenant?.products?.find(
				(product: TenantProduct) => product.type === productType
			);
			return product?.id || '';
		},
		[tenant]
	);

	const updateUserTenant = useCallback(
		async (force = false): Promise<void> => {
			if (user && tenant && !force) {
				// if user and tenant are already set, no need to fetch them again
				return;
			}
			if (isLoggedIn()) {
				setHasLoadingError(false);
				try {
					if (!tenant || force) {
						const currentTenant = await getUserTenant();
						if (currentTenant) {
							setTenant(currentTenant);
						}
					}

					if (!user || force) {
						const currentUser = await getUserMetadata();
						if (currentUser) {
							setUser(currentUser);
						}
					}
				} catch (error) {
					Logger.error('Failed to load user tenant data', error);
					setHasLoadingError(true);
					throw error;
				}
			}
		},
		[user, tenant]
	);

	const hasAdminPermissions = useCallback(
		(): boolean => !!user && user.role?.type === ROLE_TYPE_ENTITY.TENANT_ADMIN,
		[user]
	);

	useEffect(() => {
		updateUserTenant().catch(() => {
			// The provider already logged and exposed the error state.
		});
	}, [updateUserTenant]);

	const value = React.useMemo(
		() => ({ user, tenant, hasAdminPermissions, updateUserTenant, getProductIdByType }),
		[user, tenant, hasAdminPermissions, updateUserTenant, getProductIdByType]
	);
	if (hasLoadingError) {
		return <FullPageError title={USER_TENANT_PROVIDER_LABELS.loadingErrorTitle} />;
	}

	return <UserTenantContext.Provider value={value}>{children}</UserTenantContext.Provider>;
};

const useUserTenant = (): IUserTenant => {
	const context = useContext(UserTenantContext);

	if (!context) {
		throw new Error('useUserTenant must be used within UserTenantProvider');
	}

	return context;
};

export { UserTenantProvider, useUserTenant };
